import React, {useEffect, useRef} from "react";
import {AttachmentsForRenderProps, dsImageRotation} from "types/attachment";
import ImageDefault from "./Image.default.function";
import AutoSizer from 'react-virtualized/dist/es/AutoSizer';
import List from 'react-virtualized/dist/es/List';
import Menu from "./Menu.function";
import _ from "lodash";
import {IMG_BASE,} from "utils/constants";
import {useSelector} from "react-redux";
import {Store} from "redux";
import * as reduxSelectors from "components/store/application.reducers";
import {changeZoomPercentage} from "components/taskdetails-remake/taskDetails.action";
import ImageTiff from "components/taskdetails-remake/panels/attachments/attachmentTypes/Images/Image.tiff.function";

const ROW_HEIGHT = 990;
const OVERSCAN_ROW_COUNT = 4;

const TIFF = "tiff";


const Image = ({collection, uid, docVersion}: AttachmentsForRenderProps): React.ReactElement => {
    const autoSizerContainer = useRef<HTMLDivElement>(null);
    const containerWidthRef = useRef<number>(500);
    const listRef = useRef<any>(null);
    const [dsImageRotation, setDsImageRotation] = React.useState<dsImageRotation>({1: 0});
    const [pageInView, setPageInView] = React.useState<number>(1);
    const [goToPage, setGoToPage] = React.useState<number | null>(null);
    const zoom = useSelector((store: Store) => reduxSelectors.getZoomPercentage(store));

    const dsImages = useSelector((store: Store) => reduxSelectors.getImagesForRender(store));

    useEffect(() => {
        listRef.current?.recomputeRowHeights();
    }, [zoom, dsImages]);

    useEffect(() => {
        const handlePanelResize = () => {
            listRef.current?.recomputeRowHeights();
        };
        const handleWindowResize = _.debounce(() => {
            listRef.current?.recomputeRowHeights();
        }, 150);
        document.addEventListener("@@grid/refreshAttachmentsPanelLayout", handlePanelResize);
        window.addEventListener("resize", handleWindowResize);
        return () => {
            document.removeEventListener("@@grid/refreshAttachmentsPanelLayout", handlePanelResize);
            window.removeEventListener("resize", handleWindowResize);
            handleWindowResize.cancel();
        };
    }, []);


    const computeRowHeight = ({index}: any) => {
        const image = dsImages[collection.id][index];
        if (!image?.height) return ROW_HEIGHT;
        const effectiveZoom = Math.max(zoom, 50) / 100;
        if (image.width) {
            const renderedWidth = Math.min(containerWidthRef.current * effectiveZoom, image.width * effectiveZoom);
            return (renderedWidth / image.width) * image.height + 20;
        }
        return image.height * effectiveZoom || ROW_HEIGHT;
    }

    const _rowRender = ({index, key, style}: any) => {
        if (!dsImages[collection.id] || dsImages[collection.id].length === 0) return (<div/>);
        const image = dsImages[collection.id][index] || {dsURL: ""};

        return collection.mimeType.includes(TIFF) ?
            <ImageTiff src={image} style={style} key={key} zoom={zoom} id={index}
                       collectionId={collection.id}
                       uid={uid}
                       attachmentIds={collection.attachmentIds}
                       docVersion={docVersion}
                       rotation={dsImageRotation[image.pageId] ? dsImageRotation[image.pageId] : null}/> :
            <ImageDefault src={image} style={style} key={key} zoom={zoom}
                          id={index}
                          collectionId={collection.id}
                          uid={uid}
                          attachmentIds={collection.attachmentIds}
                          docVersion={docVersion}
                          rotation={dsImageRotation[image.pageId] ? dsImageRotation[image.pageId] : null}/>
    }


    const rotateImage = () => {
        const newDsImageRotation = _.cloneDeep(dsImageRotation);
        const currentRotation = newDsImageRotation[pageInView] || 0;
        const newRotation = currentRotation > 2 ? 0 : currentRotation + 1;

        newDsImageRotation[pageInView] = newRotation;
        setDsImageRotation(newDsImageRotation);
    }

    const handleScroll = (event: any) => {
        const pageHeight = zoom ? (zoom * IMG_BASE.height) / 100 : ROW_HEIGHT;
        const currentPage = Math.floor(event.scrollTop / pageHeight) + 1;
        if (currentPage !== pageInView) {
            setPageInView(currentPage);
        }
    }

    const scrollToImage = (page: number) => {
        if (!collection.images || collection.images.length === 0) return;
        if (page > 0 && page <= collection.images.length + 1) {
            setGoToPage(page);
            setPageInView(page);
        }
    }

    //const height = autoSizerContainer.current ? autoSizerContainer.current?.clientHeight - 20 : ROW_HEIGHT;
    //const width = autoSizerContainer.current ? autoSizerContainer.current?.clientWidth : IMG_BASE.width + 20;

    return (
        <div className={"w-100 h-100 images"} ref={autoSizerContainer}>
            <Menu totalImages={collection?.images?.length} rotateImage={rotateImage} scrollToImage={scrollToImage}
                  pageInView={pageInView} zoomPercentage={zoom} changeZoom={changeZoomPercentage}/>
            {collection && collection.images && collection.images.length > 0 &&
                <AutoSizer className="images-auto-sizer w-100 bg-white">
                    {({width, height}: any) => {
                        containerWidthRef.current = width || 500;
                        return (<List
                            ref={listRef}
                            className="images-auto-sizer__list"
                            height={height || 500}
                            overscanRowCount={OVERSCAN_ROW_COUNT}
                            rowCount={collection.images?.length || 0}
                            rowHeight={computeRowHeight}
                            rowRenderer={_rowRender}
                            id={"DS-Images-List" + collection.id}
                            width={width || 500}
                            scrollToAlignment="start"
                            onScroll={handleScroll}
                            scrollToIndex={goToPage ? goToPage : undefined}
                        />);
                    }}
                </AutoSizer>
            }
        </div>);
}

export default Image;